import React, { useContext, useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faPen } from '@fortawesome/free-solid-svg-icons'
import IUserManagement from '../user-management.interface'
import UserContext from '../user-context'
import { IContact } from '../user/user.interface'
import Spinner from './../../spinner/spinner'
import DashboardContext from '../../layout/dashboard-context'
import api from '../../../api'
import RoleInfo from '../role-info'

interface IUserSelect {
  fromForm: boolean
}

const UserSelect: React.FC<IUserManagement> = ({ fromForm }: IUserSelect) => {
  const { setUsers } = useContext(UserContext)
  const { contactRoles, setContactRoles } = useContext(UserContext)
  const { contacts, setContacts } = useContext(UserContext)
  const { setSelectedContact } = useContext(UserContext)
  const { selectedOrganization } = useContext(DashboardContext)

  const [filter, setFilter] = useState('')
  const contactArray: Array<IContact> = [
    {
      id: '',
      name: '',
      roles: [],
    },
  ]
  const [filteredContacts, setFilteredContacts] = useState(contactArray)

  const rolesAllowedCreate = [
    'Uppgiftslämnare för lönestatistik',
    'Företagsledare',
    'Ordförande BRF ',
    'Personalansvarig HR ansvarig',
    'Huvudkontaktperson',
  ]

  const [allowCreateDelete, setAllowCreateDelete] = useState(false)

  const checkRights = () => {
    if (selectedOrganization) {
      for (const roleIndex in selectedOrganization.roles) {
        if (rolesAllowedCreate.includes(selectedOrganization.roles[roleIndex])) {
          setAllowCreateDelete(true)
        }
      }
    }
  }

  const getUsers = async () => {
    try {
      const res = await api.get(`/accounts/${selectedOrganization.id}/users`)

      return res.data.data
    } catch (e) {
      return []
    }
  }

  const getContacts = async () => {
    try {
      const res = await api.get(`/accounts/${selectedOrganization.id}/contacts`)

      return res.data.data
    } catch (e) {
      return []
    }
  }

  const getContactRoles = async () => {
    try {
      const res = await api.get(`/enums/contact-roles`)

      return res.data.data
    } catch (e) {
      return []
    }
  }

  useEffect(() => {
    setContacts([])
    const getContactData = async () => {
      const contactData = await getContacts()
      const usersData = await getUsers()
      const contactRolesData = await getContactRoles()
      setContactRoles(contactRolesData)
      contactData.sort((a: any, b: any) => {
        if (b.name > a.name) {
          return -1
        } else {
          return 1
        }
      })
      usersData.forEach((user: any) => {
        const contact = contactData.find((contact: any) => contact.id === user.contactId)
        if (contact) {
          const contactIndex = contactData.findIndex((element: any) => element.id === contact.id)
          if (!contact.roles) {
            contact.roles = []
          }
          if (!contact.roles.includes(user.contactRole)) {
            contactData[contactIndex].roles.push(user.contactRole)
          }
        }
      })
      setContacts(contactData)
      setUsers(usersData)
    }
    getContactData()
    checkRights()
  }, [selectedOrganization])

  useEffect(() => {
    setFilteredContacts(contacts)
  }, [contacts])

  const selectContact = (contact: IContact) => {
    setSelectedContact(contact)
  }

  useEffect(() => {
    let tempArray: IContact[]
    if (filter.length > 0) {
      tempArray = contacts.filter((contact) =>
        contact.name.toLowerCase().trim().includes(filter.toLowerCase().trim()),
      )
      setFilteredContacts(tempArray)
    } else {
      setFilteredContacts(contacts)
    }
  }, [filter])

  const createContact = () => {
    setSelectedContact({
      id: 'newUser',
      name: '',
    })
  }

  return (
    <div
      className={`flex flex-col w-full bg-white gap-8 ${
        fromForm ? 'border-top' : 'card md:p-12 p-2"'
      }`}
    >
      <div className="w-full flex flex-col gap-8 d:px-6 px-2">
        <div className="flex flex-col">
          <div className="flex flex-row items-center w-full justify-between gap-8 ">
            <div className="flex justify-between flex-col">
              <h1>Användare</h1>
              {fromForm && (
                <span>
                  Vänligen se över att nedanstående information fortfarande stämmer för företaget.
                </span>
              )}
            </div>
            {allowCreateDelete && (
              <button className="btn btn-sm" onClick={() => createContact()}>
                <span className="no-underline mr-2">
                  <FontAwesomeIcon icon={faPlus} />
                </span>
                Lägg till användare
              </button>
            )}
          </div>
          <RoleInfo />
        </div>

        <div className="flex flex-col md:flex-row justify-between">
          <div className="w-full flex max-w-lg flex-col">
            <p className="label">Sök användare:</p>
            <input
              className="search"
              value={filter}
              onChange={(event) => {
                setFilter(event.target.value)
              }}
            />
          </div>
        </div>
        <div className="flex flex-wrap justify-center gap-4">
          {filteredContacts && filteredContacts.length > 0 ? (
            filteredContacts.map((contact: IContact, index: number) => {
              return (
                <div
                  key={`${contact.name}_${index}`}
                  className="m-1 flex justify-between h-auto text-xs md:text-sm border border-darkBlue rounded-lg w-full md:flex-col card-no-padding"
                >
                  <div className="p-4  w-full">
                    <div className="text-darkBlue flex flex-row justify-between align-center">
                      <div className="font-semibold  w-full text-2xl">{contact.name}</div>
                      {allowCreateDelete && (
                        <div className="flex flex-row justify-end items-center  w-full">
                          <button
                            className="btn-white btn-sm "
                            onClick={() => selectContact(contact)}
                          >
                            <span className="no-underline mr-2">
                              <FontAwesomeIcon icon={faPen} />
                            </span>
                            Hantera användare
                          </button>
                        </div>
                      )}
                    </div>
                    <ul className="flex flex-row flex-wrap gap-2 mt-2 md:mt-0">
                      {contact &&
                        contact.roles &&
                        contact.roles.map((role) => {
                          return contactRoles[role] && contactRoles[role].length > 0 ? (
                            <li key={`${role}_${index}`} className="roles-li">
                              {contactRoles[role]}
                            </li>
                          ) : null
                        })}
                    </ul>
                  </div>
                  <div className="flex items-center md:justify-center md:items-start"></div>
                </div>
              )
            })
          ) : filteredContacts && filteredContacts.length === 0 && filter.length > 0 ? (
            <p className="p-8 text-gray-600 text-sm">Inga resultat hittade för: {filter}</p>
          ) : (
            <div className="m-40">
              <Spinner />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default UserSelect
